import {
  Column,
  ColumnApi,
  ColumnGroup,
  GridApi,
  IRowNode,
} from "ag-grid-community";
import { ColumnType } from "components/AgGrid/constants";
import { createAndOpenPdf } from "lib/pdf";
import {
  formatDateString,
  formatLocalTimestampToDateTimeString,
} from "lib/timeFormats";
import greyscaleLogo from "img/AgriNous-logo-black-white-base64";

export const PdfOrientation = {
  PORTRAIT: "portrait",
  LANDSCAPE: "landscape",
} as const;

type PdfOrientationValue = (typeof PdfOrientation)[keyof typeof PdfOrientation];

interface ExportAgGridToPdfOptions {
  context?: unknown;
  orientation?: PdfOrientationValue;
  saleTitle?: string | null;
}

// Lightweight tree used to build multi-row column-group headers.
interface ColNode {
  label: string;
  children: ColNode[];
}

// pdfMake table cell — either a plain string or a content object.
type PdfCell = string | Record<string, unknown>;

const INDENT_PER_LEVEL = 12; // points per nesting level
const TOTALS_ROW_MARGIN_BOTTOM = 5; // points of space below each Totals row

// Column types that should be right-aligned, mirroring AG Grid's own behaviour.
const RIGHT_ALIGNED_TYPES = new Set([
  ColumnType.CURRENCY,
  ColumnType.CURRENCY_FROM_CENTS,
  ColumnType.NUMERIC,
  ColumnType.PERCENTAGE,
  ColumnType.RIGHT_ALIGNED,
  ColumnType.TOTAL,
  ColumnType.WEIGHT_FROM_GRAMS,
]);

// Screen pixels are 96dpi; PDF points are 72dpi.
const PIXELS_TO_POINTS = 72 / 96;

// A4 page dimensions in points (1pt = 1/72 inch).
const A4_WIDTH_PT = 595;
const A4_HEIGHT_PT = 842;

// ---------------------------------------------------------------------------
// Cell helpers
// ---------------------------------------------------------------------------

function isRightAligned(col: Column): boolean {
  const { type } = col.getColDef();
  if (!type) return false;
  const types = Array.isArray(type) ? type : [type];
  return types.some(t => RIGHT_ALIGNED_TYPES.has(t));
}

/** Plain (non-bold) data cell with optional right-alignment and indent. */
function makeDataCell(val: string, col: Column, indent: number): PdfCell {
  const right = isRightAligned(col);
  if (!right && indent === 0) return val;
  const cell: Record<string, unknown> = { text: val };
  if (right) cell.alignment = "right";
  if (indent > 0) cell.margin = [indent, 0, 0, 0];
  return cell;
}

/** Reads a cell value from the grid, applying valueFormatter if present. */
function getCellValue(
  gridApi: GridApi,
  columnApi: ColumnApi,
  col: Column,
  node: IRowNode,
  context: unknown,
): string {
  const value = gridApi.getValue(col, node);
  const colDef = col.getColDef();
  if (colDef.valueFormatter && typeof colDef.valueFormatter === "function") {
    const formatted = colDef.valueFormatter({
      value,
      data: node.data,
      node,
      column: col,
      colDef,
      api: gridApi,
      columnApi,
      context,
    } as Parameters<typeof colDef.valueFormatter>[0]);
    return formatted != null ? String(formatted) : "";
  }
  return value != null ? String(value) : "";
}

// ---------------------------------------------------------------------------
// Column header building
// ---------------------------------------------------------------------------

/**
 * Returns the deepest group-node level visible in the grid (respects
 * expand/collapse state). Returns -1 if no grouping is active.
 */
function computeMaxVisibleGroupLevel(gridApi: GridApi): number {
  let max = -1;
  const count = gridApi.getDisplayedRowCount();
  for (let i = 0; i < count; i++) {
    const node = gridApi.getDisplayedRowAtIndex(i);
    if (node?.group && node.level > max) max = node.level;
  }
  return max;
}

/**
 * Builds a tree mirroring the AG Grid column-group hierarchy.
 * getLabel resolves each leaf Column to its display-name string.
 */
function buildColTree(
  cols: Column[],
  getLabel: (col: Column) => string,
): ColNode {
  const root: ColNode = { label: "", children: [] };
  const groupMap = new Map<ColumnGroup, ColNode>();

  for (const col of cols) {
    const ancestors: ColumnGroup[] = [];
    let p: ColumnGroup | null = col.getParent();
    while (p) {
      ancestors.unshift(p);
      p = p.getParent();
    }

    let node = root;
    for (const group of ancestors) {
      let child = groupMap.get(group);
      if (!child) {
        child = {
          label: group.getColGroupDef()?.headerName ?? "",
          children: [],
        };
        groupMap.set(group, child);
        node.children.push(child);
      }
      node = child;
    }
    node.children.push({ label: getLabel(col), children: [] });
  }

  return root;
}

function nodeDepth(n: ColNode): number {
  return n.children.length === 0
    ? 0
    : 1 + Math.max(...n.children.map(nodeDepth));
}

function nodeLeafCount(n: ColNode): number {
  return n.children.length === 0
    ? 1
    : n.children.reduce((s, c) => s + nodeLeafCount(c), 0);
}

/**
 * Converts the column tree into pdfmake header rows.
 * Column groups get colSpan; leaf columns that start above the deepest
 * header row get rowSpan to fill down.
 */
function buildHeaderRows(
  cols: Column[],
  getLabel: (col: Column) => string,
): object[][] {
  const root = buildColTree(cols, getLabel);
  const numRows = nodeDepth(root);

  if (numRows <= 1) {
    return [cols.map(col => ({ text: getLabel(col), bold: true }))];
  }

  const numCols = cols.length;
  const grid: object[][] = Array.from({ length: numRows }, () =>
    Array.from({ length: numCols }, () => ({})),
  );

  function fill(node: ColNode, row: number, col: number): void {
    if (node.children.length === 0) {
      const rs = numRows - row;
      const cell: Record<string, unknown> = { text: node.label, bold: true };
      if (rs > 1) cell.rowSpan = rs;
      grid[row][col] = cell;
      for (let r = row + 1; r < numRows; r++) grid[r][col] = {};
    } else {
      const cs = nodeLeafCount(node);
      const cell: Record<string, unknown> = { text: node.label, bold: true };
      if (cs > 1) cell.colSpan = cs;
      grid[row][col] = cell;
      for (let c = 1; c < cs; c++) grid[row][col + c] = {};
      let childCol = col;
      for (const child of node.children) {
        fill(child, row + 1, childCol);
        childCol += nodeLeafCount(child);
      }
    }
  }

  let col = 0;
  for (const child of root.children) {
    fill(child, 0, col);
    col += nodeLeafCount(child);
  }

  return grid;
}

// ---------------------------------------------------------------------------
// Data row building
// ---------------------------------------------------------------------------

/**
 * Builds all data rows for the PDF table, respecting the grid's current
 * expand/collapse state. Four row types:
 *
 *   Expanded group header  — bold, group key only, no agg values
 *   Collapsed group        — plain, group key + agg values
 *   Leaf                   — plain, all column values
 *   Totals                 — bold, "[key] Totals" + agg values, spacing below
 *
 * A stack tracks open expanded groups; a Totals row is emitted for each when
 * a sibling or ancestor row is encountered. AgGrid footer nodes (groupIncludeFooter)
 * are skipped because we generate our own Totals rows.
 */
function buildDataRows(
  columns: Column[],
  gridApi: GridApi,
  columnApi: ColumnApi,
  context: unknown,
): PdfCell[][] {
  const rows: PdfCell[][] = [];
  const cv = (col: Column, node: IRowNode) =>
    getCellValue(gridApi, columnApi, col, node, context);

  // Find the column that carries the group key. With an auto-group column it's
  // always the same column regardless of level; without one, each level uses
  // its own grouped column (node.rowGroupColumn).
  const autoGroupColIdx = columns.findIndex(col =>
    col.getColId().startsWith("ag-Grid-AutoColumn"),
  );
  const getGroupKeyColIdx = (node: IRowNode): number => {
    if (autoGroupColIdx >= 0) return autoGroupColIdx;
    const groupColId = node.rowGroupColumn?.getColId();
    if (groupColId) {
      const idx = columns.findIndex(c => c.getColId() === groupColId);
      if (idx >= 0) return idx;
    }
    return 0;
  };

  const expandedGroupHeader = (node: IRowNode): PdfCell[] => {
    const indent = node.level * INDENT_PER_LEVEL;
    const keyColIdx = getGroupKeyColIdx(node);
    return columns.map((col, idx) => {
      const isKeyCol = idx === keyColIdx;
      const cell: Record<string, unknown> = {
        text: isKeyCol ? String(node.key ?? "") : "",
        bold: true,
      };
      if (isKeyCol && indent > 0) cell.margin = [indent, 0, 0, 0];
      return cell;
    });
  };

  const collapsedGroupRow = (node: IRowNode): PdfCell[] => {
    const indent = node.level * INDENT_PER_LEVEL;
    const keyColIdx = getGroupKeyColIdx(node);
    return columns.map((col, idx) => {
      const isKeyCol = idx === keyColIdx;
      return makeDataCell(
        isKeyCol ? String(node.key ?? "") : cv(col, node),
        col,
        isKeyCol ? indent : 0,
      );
    });
  };

  const leafRow = (node: IRowNode): PdfCell[] => {
    const indent = node.level * INDENT_PER_LEVEL;
    const keyColIdx = getGroupKeyColIdx(node);
    return columns.map((col, idx) =>
      makeDataCell(cv(col, node), col, idx === keyColIdx ? indent : 0),
    );
  };

  const totalsRow = (node: IRowNode): PdfCell[] => {
    const indent = (node.level + 1) * INDENT_PER_LEVEL;
    const keyColIdx = getGroupKeyColIdx(node);
    return columns.map((col, idx) => {
      const isKeyCol = idx === keyColIdx;
      const val = isKeyCol ? `${String(node.key ?? "")} Totals` : cv(col, node);
      const cell: Record<string, unknown> = {
        text: val,
        bold: true,
        margin: [isKeyCol ? indent : 0, 0, 0, TOTALS_ROW_MARGIN_BOTTOM],
      };
      if (isRightAligned(col)) cell.alignment = "right";
      return cell;
    });
  };

  const stack: IRowNode[] = [];
  const closeGroupsAbove = (level: number): void => {
    while (stack.length > 0 && level <= stack[stack.length - 1].level) {
      rows.push(totalsRow(stack.pop()!));
    }
  };

  for (let i = 0; i < gridApi.getDisplayedRowCount(); i++) {
    const node = gridApi.getDisplayedRowAtIndex(i);
    if (!node || node.footer) continue;

    closeGroupsAbove(node.level);

    if (node.group) {
      if (node.expanded) {
        rows.push(expandedGroupHeader(node));
        stack.push(node);
      } else {
        rows.push(collapsedGroupRow(node));
      }
    } else {
      rows.push(leafRow(node));
    }
  }

  closeGroupsAbove(-1);
  return rows;
}

// ---------------------------------------------------------------------------
// Main export function
// ---------------------------------------------------------------------------

export function exportAgGridToPdf(
  gridApi: GridApi,
  columnApi: ColumnApi,
  options: ExportAgGridToPdfOptions = {},
): void {
  const { context, orientation = PdfOrientation.PORTRAIT, saleTitle } = options;

  const now = new Date();
  const formattedDate = formatDateString(now);
  const printedAt = formatLocalTimestampToDateTimeString(now.getTime());

  const H_MARGIN = 15;
  const contentWidth =
    (orientation === PdfOrientation.LANDSCAPE ? A4_HEIGHT_PT : A4_WIDTH_PT) -
    H_MARGIN * 2;

  // Select columns that fit within the content area.
  // lightHorizontalLines adds 8pt padding on each side of every inter-column
  // gap, so the effective overhead is 16 * (N-1) points.
  const columns: Column[] = [];
  const widths: number[] = [];
  let totalWidth = 0;
  for (const col of columnApi.getAllDisplayedColumns()) {
    const w = parseFloat((col.getActualWidth() * PIXELS_TO_POINTS).toFixed(1));
    if (totalWidth + w + 16 * columns.length > contentWidth) break;
    totalWidth += w;
    columns.push(col);
    widths.push(w);
  }

  // Resolve the auto-group column header to the names of the row-group columns
  // that are currently visible, e.g. "Billing Role / Business" when only the
  // top level is expanded (not the full "Billing Role / Business / Document / GL Code").
  const rowGroupCols = columnApi.getRowGroupColumns();
  let autoGroupColLabel: string | null = null;
  if (rowGroupCols.length > 0) {
    const maxLevel = computeMaxVisibleGroupLevel(gridApi);
    const visibleDepth = maxLevel >= 0 ? maxLevel + 1 : 1;
    autoGroupColLabel = rowGroupCols
      .slice(0, visibleDepth)
      .map(gc => columnApi.getDisplayNameForColumn(gc, null) || gc.getColId())
      .join(" / ");
  }

  const getLabel = (col: Column): string => {
    if (col.getColId().startsWith("ag-Grid-AutoColumn") && autoGroupColLabel) {
      return autoGroupColLabel;
    }
    return columnApi.getDisplayNameForColumn(col, null) || col.getColId();
  };

  const headerRows = buildHeaderRows(columns, getLabel);
  const dataRows = buildDataRows(columns, gridApi, columnApi, context);

  // Append pinned bottom rows (grand totals) — bold, right-aligned, no indent.
  const pinnedBottomRowCount = gridApi.getPinnedBottomRowCount();
  for (let i = 0; i < pinnedBottomRowCount; i++) {
    const node = gridApi.getPinnedBottomRow(i);
    if (node) {
      dataRows.push(
        columns.map(col => {
          const val = getCellValue(gridApi, columnApi, col, node, context);
          const cell: Record<string, unknown> = { text: val, bold: true };
          if (isRightAligned(col)) cell.alignment = "right";
          return cell as PdfCell;
        }),
      );
    }
  }

  createAndOpenPdf(
    {
      pageOrientation: orientation,
      pageMargins: [15, 40, 15, 30],
      header: {
        stack: [
          {
            columns: [
              {
                text: saleTitle || "",
                alignment: "left",
                margin: [H_MARGIN, 12, 0, 2],
              },
              {
                text: formattedDate,
                alignment: "right",
                fontSize: 9,
                color: "#467cb5",
                margin: [0, 14, H_MARGIN, 2],
              },
            ],
          },
          {
            canvas: [
              {
                type: "line",
                x1: 0,
                y1: 0,
                x2: contentWidth,
                y2: 0,
                lineWidth: 1,
                lineColor: "#000000",
              },
            ],
            margin: [H_MARGIN, 0, H_MARGIN, 0],
          },
        ],
      },
      content: [
        {
          fontSize: 8,
          table: {
            headerRows: headerRows.length,
            widths,
            body: [...headerRows, ...dataRows],
          },
          layout: "lightHorizontalLines",
        },
      ],
      footer: (currentPage: number, pageCount: number) => ({
        columns: [
          {
            image: `data:image/jpeg;base64,${greyscaleLogo}`,
            fit: [60, 15],
            margin: [H_MARGIN, 5, 0, 0],
          },
          {
            text: `Printed ${printedAt} - Page ${currentPage} of ${pageCount}`,
            alignment: "right",
            margin: [0, 8, H_MARGIN, 0],
            fontSize: 8,
          },
        ],
      }),
    },
    undefined,
    true,
  );
}
